import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { CatalogItemFormDialog } from '@/components/pricebook/CatalogItemFormDialog';
import { PriceAdjustmentDialog } from '@/components/pricebook/PriceAdjustmentDialog';
import { LaborRateTiersPanel } from '@/components/pricebook/LaborRateTiersPanel';
import { useHasPermission } from '@/auth/RequirePermission';
import { Permissions } from '@/auth/permissions';
import { formatCurrency } from '@/lib/format';
import {
  CATALOG_ITEM_TYPES,
  UNCATEGORIZED,
  useCatalogCategories,
  useCatalogItems,
  useSetCatalogItemArchived,
  type CatalogFilters,
  type CatalogItem,
  type CatalogItemType,
} from '@/routes/pricebook/usePricebook';

const INITIAL_FILTERS: CatalogFilters = {
  search: '',
  type: '',
  category: '',
  includeArchived: false,
  page: 1,
};

/**
 * The price book (M2-02) — the account's flat-rate services and parts.
 *
 * <b>Read on `customers.read`, changed on `pricebook.manage`</b>, matching the
 * API. A Dispatcher sees the book (they field "how much is a water heater?"
 * on the phone) and gets no add, edit, archive or adjust affordance at all,
 * rather than buttons that render and then 403 — the rule every screen since
 * M1-37 follows.
 *
 * <b>One table, with columns hidden below `md` and restated in the name
 * cell</b> — M1-22's compact-table rule, so there is one row per item in the
 * accessibility tree rather than a table and a card list that drift.
 *
 * Categories are managed on their own screen, `/pricebook/categories`, the
 * same split `/customers/lead-sources` makes: a vocabulary you maintain now
 * and then, reached from the records it files.
 */
export function Pricebook() {
  const { t } = useTranslation();
  const canManage = useHasPermission()(Permissions.managePricebook);
  const [filters, setFilters] = useState<CatalogFilters>(INITIAL_FILTERS);
  const items = useCatalogItems(filters);
  // Retired categories included: the FILTER has to reach items still filed
  // under one (to re-file or archive them), while the add/edit and adjust
  // dialogs are handed only the active ones.
  const allCategories = useCatalogCategories(true);
  const [editing, setEditing] = useState<CatalogItem | 'new' | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [announcement, setAnnouncement] = useState('');

  function update(change: Partial<CatalogFilters>) {
    // Any filter change starts again at page 1; only the pager keeps its place.
    setFilters((previous) => ({ ...previous, page: 1, ...change }));
  }

  const hasAnyFilter =
    filters.search.trim() !== '' ||
    filters.type !== '' ||
    filters.category !== '' ||
    filters.includeArchived;
  const page = items.data;
  const totalPages = page ? Math.max(1, Math.ceil(page.totalCount / page.pageSize)) : 1;
  const categories = (allCategories.data ?? []).filter((category) => category.isActive);

  // ⚠️ Archiving the only item on the last page leaves that page empty while
  // the book is not: step back to the new last page rather than showing
  // "No items yet" with the pager gone. Not while placeholder data is showing,
  // which is the PREVIOUS page's answer.
  const strandedOnEmptyPage =
    page !== undefined && page.items.length === 0 && page.totalCount > 0 && filters.page > 1;
  useEffect(() => {
    if (strandedOnEmptyPage && !items.isPlaceholderData) {
      setFilters((previous) => ({ ...previous, page: totalPages }));
    }
  }, [strandedOnEmptyPage, items.isPlaceholderData, totalPages]);

  return (
    <section className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('pricebook.heading')}</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            {t('pricebook.description')}
          </p>
        </div>
        {canManage ? (
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/pricebook/categories" className="text-sm underline underline-offset-2">
              {t('pricebook.manageCategories')}
            </Link>
            <Button
              variant="outline"
              onClick={() => setAdjusting(true)}
              disabled={!allCategories.data}
            >
              {t('pricebook.adjustPrices')}
            </Button>
            <Button onClick={() => setEditing('new')} disabled={!allCategories.data}>
              {t('pricebook.add')}
            </Button>
          </div>
        ) : null}
      </div>

      {/* A real <form role="search">, as the customer search is: Enter on a
          phone keyboard submits instead of doing nothing. The hook's debounce
          has already run, so submitting only prevents the reload. */}
      <form
        role="search"
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => event.preventDefault()}
      >
        <label className="flex min-w-48 flex-1 flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted-foreground)]">
            {t('pricebook.filters.search')}
          </span>
          <input
            type="search"
            className={FIELD_CLASS}
            value={filters.search}
            placeholder={t('pricebook.filters.searchPlaceholder')}
            onChange={(event) => update({ search: event.target.value })}
          />
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted-foreground)]">
            {t('pricebook.filters.category')}
          </span>
          <select
            className={FIELD_CLASS}
            value={filters.category}
            onChange={(event) => update({ category: event.target.value })}
          >
            <option value="">{t('pricebook.filters.anyCategory')}</option>
            {(allCategories.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.isActive
                  ? category.label
                  : t('pricebook.retiredCategoryOption', { label: category.label })}
              </option>
            ))}
            {/* M2-01 leaves CategoryId nullable on purpose, so the filter has to
                be able to find what nobody filed. */}
            <option value={UNCATEGORIZED}>{t('pricebook.uncategorized')}</option>
          </select>
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="text-[var(--color-muted-foreground)]">
            {t('pricebook.filters.type')}
          </span>
          <select
            className={FIELD_CLASS}
            value={filters.type}
            onChange={(event) => update({ type: event.target.value as '' | CatalogItemType })}
          >
            <option value="">{t('pricebook.filters.anyType')}</option>
            {CATALOG_ITEM_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`pricebook.type.${type}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="flex items-center gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 shrink-0"
            checked={filters.includeArchived}
            onChange={(event) => update({ includeArchived: event.target.checked })}
          />
          {t('pricebook.filters.includeArchived')}
        </label>

        {hasAnyFilter ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setFilters(INITIAL_FILTERS)}
          >
            {t('pricebook.filters.clear')}
          </Button>
        ) : null}
      </form>

      {/* Said out loud, because without the categories the Add and Adjust
          buttons stay disabled and the page otherwise looks fine. */}
      {allCategories.isError ? (
        <ErrorState
          title={t('pricebook.categoriesLoadErrorTitle')}
          description={t('pricebook.categoriesLoadErrorDescription')}
          onRetry={() => void allCategories.refetch()}
        />
      ) : null}

      {items.isPending || strandedOnEmptyPage ? (
        <LoadingState label={t('pricebook.loading')} />
      ) : items.isError ? (
        <ErrorState title={t('pricebook.loadErrorTitle')} onRetry={() => void items.refetch()} />
      ) : !page || page.items.length === 0 ? (
        // ⚠️ Two different empties, said differently (M1-27's rule): "nothing
        // matches" sends the office to the filters, "nothing here yet" to the
        // Add button. One sentence for both is how somebody concludes the
        // book is empty when a filter is hiding it.
        <EmptyState
          title={hasAnyFilter ? t('pricebook.noMatchesTitle') : t('pricebook.emptyTitle')}
          description={
            hasAnyFilter ? t('pricebook.noMatchesDescription') : t('pricebook.emptyDescription')
          }
        />
      ) : (
        <>
          <p role="status" aria-live="polite" className="sr-only">
            {t('pricebook.resultCount', { count: page.totalCount })}
          </p>
          <div
            role="region"
            aria-label={t('pricebook.tableLabel')}
            tabIndex={0}
            className="overflow-x-auto rounded-lg border"
          >
            <table className="w-full text-sm" data-testid="pricebook-table">
              <thead>
                <tr className="border-b text-left">
                  <th scope="col" className="px-3 py-2 font-medium">
                    {t('pricebook.columns.name')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 font-medium md:table-cell">
                    {t('pricebook.columns.category')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 font-medium md:table-cell">
                    {t('pricebook.columns.type')}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    {t('pricebook.columns.price')}
                  </th>
                  <th scope="col" className="hidden px-3 py-2 text-right font-medium md:table-cell">
                    {t('pricebook.columns.cost')}
                  </th>
                  {canManage ? (
                    <th scope="col" className="px-3 py-2 text-right font-medium">
                      <span className="sr-only">{t('pricebook.columns.actions')}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {page.items.map((item) => (
                  <PricebookRow
                    key={item.id}
                    item={item}
                    canManage={canManage}
                    onEdit={() => setEditing(item)}
                    onAnnounce={setAnnouncement}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <p className="text-[var(--color-muted-foreground)]">
              {t('pricebook.resultCount', { count: page.totalCount })}
            </p>
            {totalPages > 1 ? (
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page.page <= 1}
                  onClick={() =>
                    setFilters((previous) => ({ ...previous, page: previous.page - 1 }))
                  }
                >
                  {t('pricebook.pagination.previous')}
                </Button>
                <span aria-live="polite">
                  {t('pricebook.pagination.pageOf', { page: page.page, totalPages })}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page.page >= totalPages}
                  onClick={() =>
                    setFilters((previous) => ({ ...previous, page: previous.page + 1 }))
                  }
                >
                  {t('pricebook.pagination.next')}
                </Button>
              </div>
            ) : null}
          </div>
        </>
      )}

      {editing ? (
        <CatalogItemFormDialog
          item={editing === 'new' ? undefined : editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onAnnounce={setAnnouncement}
        />
      ) : null}

      {adjusting ? (
        <PriceAdjustmentDialog
          categories={categories}
          initialCategoryId={filters.category}
          onClose={() => setAdjusting(false)}
          onAnnounce={setAnnouncement}
        />
      ) : null}

      {/* M2-33 (Kyle, Q1 = A): the account's tier multipliers live with the
          labor items they multiply — read by whoever reads the book, set by
          Owner and Admin. */}
      <LaborRateTiersPanel canManage={canManage} onAnnounce={setAnnouncement} />

      <p role="status" aria-live="polite" className="sr-only" data-testid="pricebook-announcement">
        {announcement}
      </p>
    </section>
  );
}

function PricebookRow({
  item,
  canManage,
  onEdit,
  onAnnounce,
}: {
  item: CatalogItem;
  canManage: boolean;
  onEdit: () => void;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const setArchived = useSetCatalogItemArchived();
  const [error, setError] = useState('');

  const category = item.categoryLabel ?? t('pricebook.uncategorized');
  const type = t(`pricebook.type.${item.type}`);

  function toggleArchived() {
    setError('');
    setArchived.mutate(
      { id: item.id, archived: item.isActive },
      {
        onSuccess: () =>
          onAnnounce(
            item.isActive
              ? t('pricebook.archived', { name: item.name })
              : t('pricebook.restored', { name: item.name }),
          ),
        onError: () => setError(t('pricebook.archiveFailed')),
      },
    );
  }

  return (
    <tr
      className="border-b last:border-b-0 align-top"
      data-testid="pricebook-row"
      data-item-id={item.id}
    >
      <td className="px-3 py-2">
        <span
          className={
            item.isActive ? 'font-medium' : 'font-medium text-[var(--color-muted-foreground)]'
          }
        >
          {item.name}
        </span>
        {/* Status is a WORD, never a colour alone (M1-09). */}
        {!item.isActive ? (
          <span className="ml-2 rounded-full border px-2 py-0.5 text-xs text-[var(--color-muted-foreground)]">
            {t('pricebook.archivedChip')}
          </span>
        ) : null}
        {/* M2-33: the one labor item a technician's override replaces. */}
        {item.isStandardLabor ? (
          <span
            className="ml-2 rounded-full border px-2 py-0.5 text-xs"
            data-testid="standard-labor-chip"
          >
            {t('pricebook.standardLaborChip')}
          </span>
        ) : null}
        {item.description ? (
          <p className="text-xs text-[var(--color-muted-foreground)]">{item.description}</p>
        ) : null}
        {/* Restated for the compact view, where these columns are hidden. */}
        <p className="text-xs text-[var(--color-muted-foreground)] md:hidden">
          {category} · {type}
        </p>
        {error ? (
          <p role="alert" className="text-sm text-[var(--color-destructive)]">
            {error}
          </p>
        ) : null}
      </td>
      <td className="hidden px-3 py-2 md:table-cell">
        {category}
        {item.categoryIsRetired ? (
          <span className="ml-1 text-xs text-[var(--color-muted-foreground)]">
            {t('pricebook.retiredCategory')}
          </span>
        ) : null}
      </td>
      <td className="hidden px-3 py-2 md:table-cell">{type}</td>
      <td className="px-3 py-2 text-right tabular-nums" data-testid="pricebook-price">
        {item.type === 'Labor'
          ? t('pricebook.perHour', { amount: formatCurrency(Number(item.defaultPrice)) })
          : formatCurrency(Number(item.defaultPrice))}
        {!item.taxable ? (
          <p className="text-xs text-[var(--color-muted-foreground)]">
            {t('pricebook.notTaxable')}
          </p>
        ) : null}
      </td>
      <td className="hidden px-3 py-2 text-right tabular-nums md:table-cell">
        {formatCurrency(Number(item.cost))}
      </td>
      {canManage ? (
        <td className="px-3 py-2 text-right">
          <span className="flex justify-end gap-3">
            <button
              type="button"
              className="text-xs underline underline-offset-2"
              aria-label={t('pricebook.editItem', { name: item.name })}
              onClick={onEdit}
            >
              {t('pricebook.edit')}
            </button>
            <button
              type="button"
              className="text-xs underline underline-offset-2"
              aria-label={
                item.isActive
                  ? t('pricebook.archiveItem', { name: item.name })
                  : t('pricebook.restoreItem', { name: item.name })
              }
              disabled={setArchived.isPending}
              onClick={toggleArchived}
            >
              {item.isActive ? t('pricebook.archive') : t('pricebook.restore')}
            </button>
          </span>
        </td>
      ) : null}
    </tr>
  );
}
